import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Main as Container } from 'Components/Main';
import { PageHelper } from 'Components/PageHelper';
import { NoData } from 'Components/NoData';
import { deleteData, getDatasPaged } from 'services/datas-service';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { Dialog, PageHeader, Button, EditSvg } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';
const BORDER = 'var(--vo-line)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';

const PAGE_SIZE = 15;

/* ── Info banner ── */
const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid ${BLUE};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;
const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
  line-height: 1.55;
`;

/* ── Table ── */
const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const THead = styled.thead`
  background: ${BG};
`;
const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`;
const Tr = styled.tr`
  border-bottom: 1px solid ${BG};
  cursor: pointer;
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;
const Td = styled.td`
  padding: 12px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
`;
const TdName = styled(Td)`
  font-size: 14px;
  color: ${INK};
  font-weight: 500;
`;
const TdMuted = styled(Td)`
  color: ${MUTED};
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ActionCell = styled.td`
  padding: 8px 16px;
  text-align: center;
`;
const ActionsWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;
/* ── Pagination ── */
const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid ${BG};
`;
const PaginationInfo = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;
const PageButtons = styled.div`
  display: flex;
  gap: 4px;
`;
const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  border: ${({ $active }) => $active ? 'none' : `1px solid ${BORDER}`};
  background: ${({ $active }) => $active ? BLUE : 'white'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '700' : '400'};
  color: ${({ $active }) => $active ? 'white' : MUTED};
  &:disabled { opacity: 0.4; cursor: default; }
`;

const getPageButtons = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
};

export const Data = () => {
  const { t } = useTranslation(['mapping']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [page, setPage] = useState(1);
  const [reFetch, setRefetch] = useState(false);

  const { isLoading, error, data: pagedDatas } = useQuery(['getData', reFetch, page], async () => {
    if (!selectedTenant.slug) return { data: [], totalPages: 0 };
    const result = await getDatasPaged(selectedTenant.slug, page, PAGE_SIZE);
    if (result?.data?.data?.length > 0) return result.data;
    return { data: [], page, totalPages: 0 };
  });

  const rows = pagedDatas?.data ?? [];
  const totalPages = pagedDatas?.totalPages ?? 0;
  const currentCount = rows.length;
  const totalItems = pagedDatas?.total ?? (totalPages <= 1 ? currentCount : totalPages * PAGE_SIZE);
  const pageButtons = getPageButtons(page, totalPages);

  const onDelete = async (item) => {
    const confirmed = await Dialog.confirm({
      title: t('mapping:dataTypes.list.deleteConfirm', { name: item.name }),
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    try {
      const res = await deleteData(item._id);
      if (res.status === 204) {
        Dialog.fire({ icon: 'success', title: t('mapping:dataTypes.notifications.deleteSuccessTitle'), text: t('mapping:dataTypes.notifications.deleteSuccessText'), timer: 1500, showConfirmButton: false });
        setRefetch(v => !v);
      } else {
        throw new Error(t('mapping:dataTypes.notifications.deleteError'));
      }
    } catch (err) {
      Dialog.alert({ title: t('mapping:dataTypes.notifications.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };

  const tourSteps = [
    { target: 'body', content: t('mapping:dataTypes.tour.list.welcome'), disableBeacon: true, disableScrolling: true, placement: 'center' },
    { target: '[data-tour="add-btn"]', content: t('mapping:dataTypes.tour.list.addButton'), disableScrolling: true },
    { target: '[data-tour="info-banner"]', content: t('mapping:dataTypes.tour.list.infoCard'), disableScrolling: true },
    { target: '[data-tour="table"]', content: t('mapping:dataTypes.tour.list.table'), disableScrolling: false },
    { target: 'body', content: t('mapping:dataTypes.tour.list.complete'), disableScrolling: true, placement: 'center' },
  ];

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="data-list" />

      <PageHeader
        title={t('mapping:dataTypes.list.title')}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('mapping:dataTypes.list.title') },
        ]}
        actions={
          <Button
            variant="primary"
            data-tour="add-btn"
            onClick={() => navigate('/data/add')}
            startIcon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            }
            sx={{ height: '44px', padding: '0 20px' }}
          >
            {t('mapping:dataTypes.list.addButton')}
          </Button>
        }
      />

      <InfoBanner data-tour="info-banner">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <ellipse cx="12" cy="5" rx="9" ry="3"/>
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
        </svg>
        <div>
          <InfoTitle>{t('mapping:dataTypes.list.infoTitle')}</InfoTitle>
          <InfoDesc>{t('mapping:dataTypes.list.infoDescription')}</InfoDesc>
        </div>
      </InfoBanner>

      {isLoading && null}
      {error && <div style={{ padding: 24, fontFamily: 'Lato', fontSize: 14, color: MUTED }}>{t('mapping:dataTypes.list.error')}</div>}
      {!isLoading && !error && rows.length === 0 && <NoData description={t('mapping:dataTypes.list.noData')} />}

      {!isLoading && !error && rows.length > 0 && (
        <TableCard data-tour="table">
          <Table>
            <THead>
              <tr>
                <Th>{t('mapping:dataTypes.table.name')}</Th>
                <Th>{t('mapping:dataTypes.table.description')}</Th>
                <Th $center>{t('mapping:dataTypes.table.actions')}</Th>
              </tr>
            </THead>
            <tbody>
              {rows.map(item => (
                <Tr key={item._id} onClick={() => navigate('/data/show/' + item._id)}>
                  <TdName>{item.name}</TdName>
                  <TdMuted title={item.description}>{item.description}</TdMuted>
                  <ActionCell onClick={e => e.stopPropagation()}>
                    <ActionsWrap>
                      <Button iconOnly variant="icon" title="Ver detalhes" onClick={() => navigate('/data/show/' + item._id)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                      </Button>
                      <Button iconOnly variant="icon" title={t('common:tooltips.edit')} onClick={() => navigate('/data/edit/' + item._id)}>
                        <EditSvg />
                      </Button>
                      <Button iconOnly variant="icon" danger title={t('common:tooltips.delete')} onClick={() => onDelete(item)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                        </svg>
                      </Button>
                    </ActionsWrap>
                  </ActionCell>
                </Tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 0 && (
            <PaginationRow>
              <PaginationInfo>
                {t('mapping:dataTypes.list.pagination.showing', { count: currentCount, total: totalItems })}
              </PaginationInfo>
              <PageButtons>
                <PageBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </PageBtn>
                {pageButtons.map((btn, i) =>
                  btn === '…'
                    ? <PageBtn key={`e${i}`} disabled style={{ cursor: 'default' }}>…</PageBtn>
                    : <PageBtn key={btn} $active={btn === page} onClick={() => setPage(btn)}>{btn}</PageBtn>
                )}
                <PageBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </PageBtn>
              </PageButtons>
            </PaginationRow>
          )}
        </TableCard>
      )}
    </Container>
  );
};
